import { action, makeObservable, observable } from 'mobx';
import { PasswordRecoveryForm } from 'modules/membership/forms';
import { BaseViewStore } from 'core/stores';
import FulfillmentPasswordStore from './FulfillmentPasswordStore';
import { formInvalidate, notificationError } from 'core/utils';
import { localStorageHandler } from 'core/services';
import _ from 'lodash';

class AppPasswordRecoveryViewStore extends BaseViewStore {
	@observable smsOption = true; // SMS is default option.
	@observable resendMessage = null;
	@observable timer = null;
	@observable errorMessage = null;
	@observable emailSent = false;
	verificationCodeSent = false;
	resettedUserId = null;
	timerData;
	form = new PasswordRecoveryForm({
		onSuccess: async () => {
			await this.sendResetPasswordCodeOrEmail();
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.passwordStore = new FulfillmentPasswordStore(rootStore);
		this.timerData = localStorageHandler.get('reset-password-2fa-timer');
		if (this.timerData != null) {
			if (this.timerData[0]) {
				this.initializeTimer(this.timerData[0]);
			}
		}
	}

	@action.bound
	onToggleVerificationOption = smsOption => {
		this.smsOption = smsOption;
	};

	@action.bound
	resetTimerData = () => {
		let timerList = localStorageHandler.get('reset-password-2fa-timer');
		if (timerList != null) {
			if (timerList.length == 0) {
				localStorageHandler.remove('reset-password-2fa-timer');
			} else {
				localStorageHandler.set('reset-password-2fa-timer', timerList);
			}
			this.timer = null;
			this.errorMessage = null;
		}
	};

	@action.bound
	initializeTimer = timerData => {
		this.errorMessage = timerData.message;
		const currentTime = Date.now();
		const delta = parseInt(timerData.endTime, 10) - currentTime;
		if (delta <= 0) {
			this.resetTimerData();
		} else {
			this.timer = {
				date: currentTime,
				delay: delta,
			};
		}
	};

	@action.bound
	composeTimerList(message, currentTime, delay) {
		let timerList = localStorageHandler.get('reset-password-2fa-timer');
		if (timerList == null || timerList.length == 0) {
			timerList = [];
		}

		timerList.push({
			message: message,
			endTime: JSON.stringify(currentTime + delay),
		});
		localStorageHandler.set('reset-password-2fa-timer', timerList);
	}

	setTimerData = message => {
		const delay = 600000;
		const currentTime = Date.now();
		this.composeTimerList(message, currentTime, delay);
		this.timer = {
			date: currentTime,
			delay: delay,
		};
	};

	@action.bound
	sendResetPasswordCodeOrEmail = async () => {
		this.verificationCodeSent ? this.sendResetPasswordEmail() : this.sendResetPasswordCode();
	};

	@action.bound
	sendResetPasswordCode = async resend => {
		this.suspend();
		try {
			if (resend) {
				this.errorMessage = null;
				this.resendMessage = 'TWOSTEPAUTHENTICATION.CODERESENT';
			} else {
				this.resendMessage = null;
			}
			const { organizationIdentifier, usernameOrEmail } = this.form.values();
			const response = await this.passwordStore.sendResetPasswordCode({
				organizationIdentifier,
				usernameOrEmail,
				useSms: this.smsOption,
			});
			this.verificationCodeSent = response.isSuccess;
			this.errorMessage = null;
			this.form.$('verificationCode').set('rules', 'required');
		} catch (err) {
			this.resendMessage = null;
			this.handleRecoveryError(err);
		}
		this.resume();
	};

	@action.bound
	sendResetPasswordEmail = async () => {
		this.suspend();
		try {
			let url = `${window.location.origin}/password-change`;
			const { organizationIdentifier, usernameOrEmail, verificationCode } = this.form.values();
			const response = await this.passwordStore.sendResetPasswordEmail({
				organizationIdentifier,
				usernameOrEmail,
				url,
				verificationCode,
			});
			if (response.isSuccess) {
				localStorageHandler.remove('reset-password-2fa-timer');
				this.rootStore.notificationStore.success([response.message]);
				this.emailSent = true;
				this.resettedUserId = response.userId;
			}
		} catch (err) {
			this.handleRecoveryError(err);
		}

		this.resume();
	};

	@action.bound
	handleRecoveryError = err => {
		if (err.data) {
			if (err.data.errorMessage) {
				if (
					err.data.errorMessage == 'TWOSTEPAUTHENTICATION.MAXCHECKATTEMPTSREACHED' ||
					err.data.errorMessage == 'TWOSTEPAUTHENTICATION.MAXSENDATTEMPTSREACHED'
				) {
					this.verificationCodeSent = false;
					this.form.$('verificationCode').set('rules', '');
					this.setTimerData(err.data.errorMessage);
				}
				this.errorMessage = err.data.errorMessage;
			} else {
				err.data.errorMessage = 'PASSWORDRECOVERYFORM.GENERICERROR';
				formInvalidate({ form: this.form, err });
			}
		} else {
			this.rootStore.notificationStore.error(['PASSWORDRECOVERYFORM.GENERICERROR']);
		}
	};
	@action.bound
	resendResetPasswordEmail = async () => {
		this.suspend();
		try {
			let url = `${window.location.origin}/password-change`;
			const { organizationIdentifier } = this.form.values();
			const response = await this.passwordStore.resendResetPasswordEmail({
				organizationIdentifier,
				userId: this.resettedUserId,
				url,
			});
			if (response.isSuccess) {
				localStorageHandler.remove('reset-password-2fa-timer');
				this.rootStore.notificationStore.success([response.message]);
				this.emailSent = true;
			}
		} catch (err) {
			if (err.data && err.data.errorMessage) {
				this.rootStore.notificationStore.error([err.data.errorMessage]);
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}

		this.goToLogin();
		this.resume();
	};

	@action.bound
	goToLogin = () => {
		this.form.clear();
		this.verificationCodeSent = false;
		this.emailSent = false;
		this.rootStore.routerStore.goTo('master.app.membership.login');
	};
}

export default AppPasswordRecoveryViewStore;
